import { useState, useCallback, useRef, useEffect } from "react";
import {
  agentPost,
  FingerprintTemplatesType,
  getFingerprintTemplateById,
  getFingerprintTemplates,
  postFingerprintTemplate,
  WS_RECONNECT_DELAY_MS,
  WS_URL
} from "../api/fingerprintTemplates";
import { Client } from "../interfaces/ClientInterface";

export type EnrollState = {
  active: boolean;
  done: boolean;
  success: boolean;
  samplesCollected: number;
  samplesRequired: number;
  message: string;
  templateBase64: string | null;
};

export type IdentifyState =
  | { status: "idle" }
  | { status: "loading_templates" }
  | { status: "waiting" }
  | { status: "matched"; fid: number; score: number; clientId?: number; client: Client }
  | { status: "no_match" }
  | { status: "error"; message: string };

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const useFingerprint = () => {
  const [deviceReady, setDeviceReady] = useState(false);
  const [imageB64, setImageB64] = useState<string | null>(null);
  const [enrollState, setEnrollState] = useState<EnrollState | null>(null);
  const [identifyState, setIdentifyState] = useState<IdentifyState>({ status: "idle" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  const connectWs = useCallback(() => {
    if (!mountedRef.current) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;

    ws.onmessage = async (ev: MessageEvent<string>) => {
      let msg: any;
      try {
        msg = JSON.parse(ev.data);
      } catch {
        return;
      }

      switch (msg.type) {
        case "image":
          if (msg.imageBase64) setImageB64(msg.imageBase64);
          break;

        case "enroll_status":
          setEnrollState({
            active: msg.active,
            done: msg.done,
            success: msg.success,
            samplesCollected: msg.samplesCollected,
            samplesRequired: msg.samplesRequired,
            message: msg.message,
            templateBase64: msg.templateBase64 ?? null
          });
          break;

        case "identify_result": {
          if (msg.pending) break;

          if (!msg.success) {
            setIdentifyState({ status: "no_match" });
            break;
          }

          try {
            const fp = await getFingerprintTemplateById(msg.fid);
            setIdentifyState({
              status: "matched",
              fid: msg.fid,
              score: msg.score,
              clientId: fp.clientId,
              client: fp.client
            });
          } catch (e: any) {
            setIdentifyState({ status: "error", message: e.message });
          }
          break;
        }

        case "ping":
          break;

        default:
          break;
      }
    };

    ws.onclose = () => {
      if (!mountedRef.current) return;
      reconnectTimerRef.current = setTimeout(connectWs, WS_RECONNECT_DELAY_MS);
    };

    ws.onerror = (ev) => {
      console.log("ws error:", ev);
    };
  }, []);

  const disconnectWs = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  // ── Device ───

  const initialize = async () => {
    setLoading(true);
    setError(null);
    try {
      await agentPost("/initialize");
      setDeviceReady(true);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const terminate = async () => {
    try {
      await agentPost("/terminate");
    } catch (e) {
      console.log(e);
    }
    setDeviceReady(false);
    setImageB64(null);
  };

  // ── Mount / unmount ───

  useEffect(() => {
    if (!deviceReady) {
      initialize();
    }
    mountedRef.current = true;
    connectWs();
    return () => {
      terminate();
      mountedRef.current = false;
      disconnectWs();
    };
  }, [connectWs, disconnectWs]);

  // ── Enroll ───

  const startEnroll = async () => {
    setError(null);
    try {
      await agentPost("/enroll/start");
      setEnrollState(null);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const cancelEnroll = async () => {
    await agentPost("/enroll/cancel").catch(console.log);
    setEnrollState(null);
  };

  const saveEnrolledTemplate = async (
    clientId: number,
    templateBase64: string
  ): Promise<FingerprintTemplatesType | null> => {
    setLoading(true);
    setError(null);
    try {
      const data = await postFingerprintTemplate({ clientId, templateBase64 });
      await agentPost("/enroll/acknowledge", { fid: data?.id });
      setEnrollState(null);
      return data;
    } catch (e: any) {
      setError(e.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // ── Identify ───

  const identify = useCallback(async () => {
    setError(null);
    setIdentifyState({ status: "loading_templates" });

    try {
      const data = await getFingerprintTemplates();
      if (!data?.length) {
        setIdentifyState({ status: "error", message: "No templates in database" });
        return;
      }

      await agentPost("/templates/load", {
        templates: data.map((t: any) => ({
          fid: t.fid,
          templateBase64: t.template_base64
        }))
      });

      setIdentifyState({ status: "waiting" });
    } catch (e: any) {
      setError(e.message);
      setIdentifyState({ status: "error", message: e.message });
    }
  }, []);

  const resetIdentify = () => setIdentifyState({ status: "idle" });

  return {
    deviceReady,
    imageB64,
    enrollState,
    identifyState,
    loading,
    error,
    initialize,
    terminate,
    startEnroll,
    cancelEnroll,
    saveEnrolledTemplate,
    identify,
    resetIdentify
  };
};

export default useFingerprint;
